import {Form, Formik, FormikErrors} from 'formik';
import Label from 'shared/components/label';
import FormikDropdown from '../formik-dropdown';
import FormikTextField from '../FormikTextField';
import React, {useEffect, useMemo, useState} from 'react';
import AssetService from 'src/services/AssetService';
import Modal from 'shared/components/modal';
import AccessOrganizationFields from './AccessOrganizationFields';
import {FormOption} from 'src/types/form';
import {capitalize} from 'lodash';
import {FooterButtonType} from 'shared/components/modal/footer-button';

interface GetTextProps {
  subtitleText: string;
  countryText: string;
  subdivisionText: string;
  cityText: string;
  schoolNameText: string;
}

interface RequestInstitutionFormValues {
  country: string;
  city: string;
  name: string;
  state: string;
  schoolType: string;
  website: string;
  address: string;
  phone?: string;
}

interface RequestInstitutionFormProps {
  userId: string;
  impostorMode: boolean;
  firstName: string;
  type: string;
  onSubmit: (values: RequestInstitutionFormValues) => void;
  handleClose: () => void;
  deleteStudent: (_id: string) => void;
}

const getText = (type: string, impostorMode: boolean, firstName: string): GetTextProps => {
  const pronoun = impostorMode ? `${firstName}'s` : 'your';
  const schoolOrOrg = type === 'Mentor' ? 'access organization' : 'school';

  return {
    subtitleText: `Most ${schoolOrOrg}s in the U.S. are eligible for Raise Me, but not all of them are on our list. Tell us
    more, and we'll look into adding your ${schoolOrOrg} within 1 business day.`,
    countryText: `Country of ${pronoun} ${schoolOrOrg}`,
    subdivisionText: `State of ${pronoun} ${schoolOrOrg}`,
    cityText: `City of ${pronoun} ${schoolOrOrg}`,
    schoolNameText: `Name of ${pronoun} ${schoolOrOrg}`,
  };
};

function validateForm(
  values: RequestInstitutionFormValues
): FormikErrors<RequestInstitutionFormValues> {
  return Object.keys(values).reduce(
    (errorsObject: FormikErrors<RequestInstitutionFormValues>, propertyName: string) => {
      if (!values[propertyName as keyof RequestInstitutionFormValues]) {
        errorsObject[propertyName as keyof RequestInstitutionFormValues] = `${capitalize(
          propertyName
        )} is required`;
      }

      return errorsObject;
    },
    {} as FormikErrors<RequestInstitutionFormValues>
  );
}

export default function RequestInstitutionForm(
  props: RequestInstitutionFormProps
): React.ReactElement {
  const [countries, setCountries] = useState<FormOption[]>([]);
  const [subdivisions, setSubdivisions] = useState<FormOption[]>([]);

  useEffect(() => {
    AssetService.getCountryOptions().then(setCountries);
  }, []);

  useEffect(() => {
    AssetService.getSubdivisionOptions().then(setSubdivisions);
  }, []);

  const submitFormProps = (values: RequestInstitutionFormValues): FooterButtonType => ({
    onClick: (): void => props.onSubmit(values),
    text: 'Send',
    type: 'submit',
    cy: 'requestInstitution-submit',
  });

  const isMentor = props.type === 'Mentor';

  const initialValues = useMemo(() => {
    const values = {
      country: 'US',
      city: '',
      state: '',
      name: '',
    } as RequestInstitutionFormValues;

    if (isMentor) {
      // mentors have two additional form fields and an access org schoolType
      values.schoolType = 'access_organization';
      values.website = '';
      values.address = '';
      values.phone = '';
    } else if (props.type === 'transfer') {
      // default schoolType is 'high school'
      values.schoolType = 'community_college';
    }

    return values;
  }, [props.type]);

  const text = getText(props.type, props.impostorMode, props.firstName);

  return (
    <Formik<RequestInstitutionFormValues>
      validateOnBlur
      initialValues={initialValues}
      onSubmit={props.onSubmit}
      validate={validateForm}
    >
      {({values}) => {
        const disableFields = values.country !== 'US';
        return (
          <Form>
            <Modal.TextHeader onClose={props.handleClose} subtitle={text.subtitleText}>
              Send a Request
            </Modal.TextHeader>
            <Modal.Body>
              <Label required>
                {text.countryText}
                <FormikDropdown cy="country" testid="country" options={countries} name="country" />
              </Label>
              {disableFields && (
                <p className="shared-error-text error-message">
                  You must attend college in the U.S. to use Raise Me for transfer students.
                </p>
              )}
              {isMentor && <AccessOrganizationFields />}
              <Label required>
                {text.subdivisionText}
                <FormikDropdown
                  options={subdivisions}
                  name="state"
                  cy="state"
                  testid="state"
                  disabled={disableFields}
                />
              </Label>
              <Label required>
                {text.cityText}
                <FormikTextField
                  type="text"
                  required
                  showCheck
                  placeholder="e.g. San Francisco"
                  disabled={disableFields}
                  name="city"
                  cy="city"
                  aria-label="City"
                  data-testid="city"
                />
              </Label>
              <Label required>
                {text.schoolNameText}
                <FormikTextField
                  type="text"
                  cy="schoolName"
                  required
                  showCheck
                  disabled={disableFields}
                  name="name"
                  placeholder="e.g. Martin Luther King Community College"
                  aria-label="School or organization"
                  data-testid="schoolName"
                />
              </Label>
            </Modal.Body>
            <Modal.Footer
              primaryButton={
                disableFields
                  ? {
                      onClick: (): void => props.deleteStudent(props.userId),
                      text: 'Delete Account',
                      cy: 'requestInstitution-deleteAccount',
                      color: 'red',
                    }
                  : submitFormProps(values)
              }
            />
          </Form>
        );
      }}
    </Formik>
  );
}
